import { useState, useEffect } from "react";
import { Sparkles, Lock, Loader2, Copy } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";

export default function Drafts() {
  const [topic, setTopic] = useState("");
  const [instructions, setInstructions] = useState("");
  const [length, setLength] = useState("medium");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [quota, setQuota] = useState(null);

  useEffect(() => { api.get("/storage/quota").then(r => setQuota(r.data)); }, []);

  const generate = async () => {
    if (!topic.trim()) { toast.error("Topic required"); return; }
    setBusy(true); setDraft("");
    try {
      const r = await api.post("/ai/draft", { topic, instructions, length });
      setDraft(r.data.draft);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(draft);
    toast.success("Copied to clipboard");
  };

  if (quota && !quota.allow_drafts) {
    return (
      <div className="p-6 sm:p-8 lg:p-12 max-w-2xl">
        <div className="nb-card p-8 text-center">
          <Lock className="w-12 h-12 mx-auto" strokeWidth={2.5}/>
          <h1 className="font-display text-3xl font-black mt-3">AI assignment drafts is a Pro Plus feature</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
            Get help shaping the structure of essays and assignments. Upgrade to Pro Plus to use it (£12.99/mo).
          </p>
          <Link to="/pricing" className="nb-btn-primary mt-6 inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4" strokeWidth={2.5}/> See plans
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-4xl">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Pro · AI Draft</p>
        <h1 className="font-display text-4xl font-black mt-1">Assignment draft assistant</h1>
        <p className="text-base font-medium text-slate-600 mt-2">A starting point — not a finished essay. Always edit, fact-check, and write in your own voice.</p>
      </header>

      <div className="nb-card p-6 space-y-4">
        <div>
          <label className="text-xs uppercase font-bold tracking-widest">Topic</label>
          <input data-testid="draft-topic" value={topic} onChange={(e)=>setTopic(e.target.value)} placeholder="e.g. The causes of the French Revolution" className="nb-input mt-1"/>
        </div>
        <div>
          <label className="text-xs uppercase font-bold tracking-widest">Extra instructions (optional)</label>
          <textarea data-testid="draft-instructions" value={instructions} onChange={(e)=>setInstructions(e.target.value)} rows={2} placeholder="Tone, audience, points to cover" className="nb-input mt-1"/>
        </div>
        <div>
          <label className="text-xs uppercase font-bold tracking-widest">Length</label>
          <select data-testid="draft-length" value={length} onChange={(e)=>setLength(e.target.value)} className="nb-input mt-1 max-w-[200px]">
            <option value="short">Short (~150 words)</option>
            <option value="medium">Medium (~350 words)</option>
            <option value="long">Long (~700 words)</option>
          </select>
        </div>
        <button onClick={generate} disabled={busy} data-testid="draft-generate-btn" className="nb-btn-primary inline-flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5}/> : <Sparkles className="w-4 h-4" strokeWidth={2.5}/>}
          {busy ? "Drafting…" : "Generate draft"}
        </button>
      </div>

      {draft && (
        <div className="nb-card p-6 mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl font-bold">Draft</h2>
            <button onClick={copy} className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3"><Copy className="w-3 h-3" strokeWidth={2.5}/> Copy</button>
          </div>
          <div className="prose max-w-none whitespace-pre-wrap text-slate-800 leading-relaxed">{draft}</div>
          <div className="mt-5 nb-card-sm p-3" style={{ background: "#FDE04733" }}>
            <p className="text-xs font-medium">⚠️ AI can make mistakes. Verify all facts and rewrite in your own voice before submitting.</p>
          </div>
        </div>
      )}
    </div>
  );
}
